import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    LabelList,
} from "recharts";
import { CSSProperties, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
    DisaggregatedMetric,
    GeoDetails,
    GeoTag,
    MetricMetadata,
} from "../../services/types";
import {
    calculateGeoPrefix,
    fetchMetricMetadata,
    fetchNationalData,
    fetchStateData,
    getGeoPrefix,
} from "../../utils/dashboardUtils";
import { formatValue, formatValueBase, getReadableTextColor } from "../../utils/helpers";
import { useDashboardColor } from "../../context/DashboardColorContext";

import MetricMetadataModal from "../MetricMetadataModal";
import metricMetadataIcon from "../../assets/metric-metadata-icon.svg";
import DefaultIcon from "../../assets/svgr/default-metric.svg?react";
import GradientBar from "../GradientBar";

interface EthnicityBarChartProps {
    metric: DisaggregatedMetric | undefined;
    populationMetric: DisaggregatedMetric | undefined;
    svg?: React.FC<React.SVGProps<SVGSVGElement>> | null;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    height: string;
    background: string;
    showMetadataIcon?: boolean;
    reverseOrder?: boolean;
    subText?: string;
    showValue?: boolean;
    titleOverride?: string;
}

const normalizeLabel = (value: string) =>
    value
        .replace(/\u00A0/g, " ")
        .replace(/\s+/g, " ")
        .replace(/\bNH\b/g, "Non-Hispanic")
        .trim();

const formatShareLabel = (share: number | null) => {
    if (share === null) return "";
    return `${Math.round(share)}%`;
};

const CustomTooltipBar = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0].payload;

    return (
        <div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
            <p className="font-semibold">{normalizeLabel(label)}</p>
            <p>{`Value: ${formatValue(row.rawValue, row.unit, row.metricId)}`}</p>
            {row.share !== null && <p>{`Share: ${formatShareLabel(row.share)}`}</p>}
        </div>
    );
};

const StartLabel = (color: string) => (props: any) => {
    const { x, y, width, height, value } = props;

    if (!value) return null;

    const text = String(value);
    const estimatedTextWidth = text.length * 7;
    const padding = 12;
    const minWidth = estimatedTextWidth + padding;

    if (width < minWidth) return null;

    const textColor = getReadableTextColor(color);

    return (
        <text
            x={x + 8}
            y={y + height / 2}
            dy={4}
            textAnchor="start"
            fill={textColor}
            style={{ fontSize: 11, fontWeight: 700 }}
        >
            {text}
        </text>
    );
};

const EndLabel = (props: any) => {
    const { x, y, width, height, value } = props;

    if (!value) return null;

    return (
        <text
            x={x + width + 8}
            y={y + height / 2}
            dy={4}
            textAnchor="start"
            fill="#334155"
            style={{ fontSize: 11, fontWeight: 600 }}
        >
            {value}
        </text>
    );
};

const CustomTick = (props: any) => {
    const { x, y, payload } = props;
    const maxLineLength = 20;
    const raw = typeof payload.value === "string" ? payload.value : String(payload.value);

    const label = raw.replace(/\u00A0/g, " ").replace(/\s+/g, " ").trim();

    const words = label.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((word: string) => {
        if ((currentLine + word).length <= maxLineLength) {
            currentLine += (currentLine ? " " : "") + word;
        } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
        }
    });

    if (currentLine) lines.push(currentLine);

    if (lines.length === 1) {
        return (
            <text
                x={x}
                y={y}
                dy={2}
                textAnchor="end"
                fill="#666"
                style={{ whiteSpace: "nowrap", fontSize: 10 }}
            >
                {lines[0]}
            </text>
        );
    }

    return (
        <text x={x} y={y} textAnchor="end" fill="#666" style={{ fontSize: 10 }}>
            {lines.map((line, i) => (
                <tspan key={i} x={x} dy={i === 0 ? -1 : 9}>
                    {line}
                </tspan>
            ))}
        </text>
    );
};

const EthnicityBarChart = ({
    metric,
    populationMetric,
    svg,
    disaggData,
    geoDetails,
    geoTag,
    height,
    background,
    showMetadataIcon = true,
    reverseOrder = false,
    subText,
    showValue = false,
    titleOverride,
}: EthnicityBarChartProps) => {
    const color = useDashboardColor();
    const IconComponent = svg || DefaultIcon;

    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [stateData, setStateData] = useState<DisaggregatedMetric[] | null>(null);
    const [nationalData, setNationalData] = useState<DisaggregatedMetric[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    if (
        !metric ||
        !metric.versions ||
        metric.versions.length === 0 ||
        !populationMetric ||
        !populationMetric.versions ||
        populationMetric.versions.length === 0
    ) {
        return (
            <div className="h-24 flex items-center justify-center text-xs text-slate-500">
                <Skeleton height={24} width={"100%"} />
            </div>
        );
    }

    const id = metric.id;
    const name = metric.name;
    const version = metric.versions[0];

    const populationVersion = populationMetric.versions[0];

    const unit = version?.unit ?? populationVersion?.unit ?? "integer";

    const totalPopulation = Number(populationVersion?.value ?? 0);

    // Ethnicity indicator value now represents the Hispanic/Latino population.
    const hispanicValue = Number(version?.value ?? 0);

    const nonHispanicValue = Math.max(
        0,
        totalPopulation - hispanicValue
    );

    const modalId = `metric-metadata-modal-${id ?? "main"}`;

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        const fetchPromises: Promise<void>[] = [];

        if (!metadata) {
            fetchPromises.push(
                fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)).then((res) => {
                    setMetadata(res?.data ?? null);
                })
            );
        }

        if (version.unit === "percentage" || version.unit === "ratio") {
            const geoPrefix = geoTag?.value
                ? getGeoPrefix(geoTag.value)
                : stateData
                    ? null
                    : calculateGeoPrefix(geoDetails);

            if (!stateData && geoPrefix) {
                fetchPromises.push(
                    fetchWithCache(`stateData-${id}-${geoPrefix}`, () =>
                        fetchStateData(id, false, geoPrefix)
                    ).then((res) => {
                        setStateData(res?.data ?? null);
                    })
                );
            }

            if (!nationalData) {
                fetchPromises.push(
                    fetchWithCache(`nationalData-${id}`, () =>
                        fetchNationalData(id, false)
                    ).then((res) => {
                        setNationalData(res?.data ?? null);
                    })
                );
            }
        }

        if (fetchPromises.length > 0) {
            setLoading(true);
            try {
                await Promise.all(fetchPromises);
            } catch (error) {
                const errorMessage =
                    (error as Error).message || "An unexpected error occurred.";
                setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
                setLoading(false);
            }
        }
    };

    const chartData = [
        {
            name: "Hispanic or Latino",
            value: hispanicValue,
            rawValue: hispanicValue,
            share:
                totalPopulation > 0
                    ? (hispanicValue / totalPopulation) * 100
                    : null,
            shareLabel:
                totalPopulation > 0
                    ? formatShareLabel(
                        (hispanicValue / totalPopulation) * 100
                    )
                    : "",
            valueLabel: formatValue(
                hispanicValue,
                unit,
                id
            ),
            unit,
            metricId: id,
            versionValue: totalPopulation,
        },
        {
            name: "Non Hispanic or Latino",
            value: nonHispanicValue,
            rawValue: nonHispanicValue,
            share:
                totalPopulation > 0
                    ? (nonHispanicValue / totalPopulation) * 100
                    : null,
            shareLabel:
                totalPopulation > 0
                    ? formatShareLabel(
                        (nonHispanicValue / totalPopulation) * 100
                    )
                    : "",
            valueLabel: formatValue(
                nonHispanicValue,
                unit,
                id
            ),
            unit,
            metricId: id,
            versionValue: totalPopulation,
        },
    ];

    const displayData = reverseOrder ? [...chartData].reverse() : chartData;

    return (
        <>
            <div className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white">
                <div className="p-3 bg-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="flex items-center gap-2">
                                {svg && (
                                    <div
                                        className="inline-flex flex-shrink-0 rounded-lg p-1.5 transition-colors bg-[var(--bg)] group-hover:bg-[var(--bg-hover)]"
                                        style={
                                            {
                                                "--bg": `${color}20`,
                                                "--bg-hover": `${color}40`,
                                            } as CSSProperties
                                        }
                                    >
                                        <IconComponent
                                            className="w-8 h-8"
                                            style={{ color }}
                                            aria-label={`${name} icon`}
                                            role="img"
                                        />
                                    </div>
                                )}

                                <h3 className="text-sm font-semibold text-color-cai-brown">
                                    {titleOverride ?? name}
                                </h3>
                            </div>

                            <div className="flex items-center">
                                <p className="text-color-cai-brown text-xs mr-2">{subText}</p>
                                <p className="text-color-cai-brown text-xs font-bold">
                                    {showValue && totalPopulation > 0 && formatValueBase(totalPopulation, unit)}
                                </p>
                            </div>
                        </div>

                        {showMetadataIcon && (
                            <button
                                ref={triggerRef}
                                type="button"
                                onClick={handleClick}
                                aria-haspopup="dialog"
                                aria-expanded={showMetadata ? "true" : "false"}
                                aria-controls={modalId}
                                aria-label={`Additional information about ${titleOverride}`}
                                className="
                                    inline-flex h-6 w-6 items-center justify-center rounded
                                    transition-transform hover:scale-105 focus:scale-105
                                    focus-visible:outline-none
                                    focus-visible:ring-2 focus-visible:ring-offset-2
                                    focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                                    dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                                    "
                            >
                                <img
                                    src={metricMetadataIcon}
                                    alt=""
                                    aria-hidden="true"
                                    className="h-3 w-3"
                                    draggable="false"
                                />
                            </button>
                        )}
                    </div>
                </div>

                <div className={`${height} ${background} min-h-[120px] min-w-0`}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={120}>
                        <BarChart
                            data={displayData}
                            layout="vertical"
                            margin={{ top: 2, right: 75, left: 25, bottom: 0 }}
                            barCategoryGap="8%"
                        >
                            <XAxis
                                type="number"
                                domain={[0, totalPopulation]}
                                tick={{ fontSize: 10 }}
                                tickFormatter={(tickValue) => formatValue(tickValue, unit, id)}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                tick={<CustomTick />}
                                width={100}
                                interval={0}
                                tickLine={false}
                            />
                            <Tooltip content={<CustomTooltipBar />} cursor={{ fill: "transparent" }} />
                            <Bar dataKey="value" fill={color} activeBar={false} barSize={20}>
                                <LabelList dataKey="shareLabel" content={StartLabel(color)} />
                                <LabelList dataKey="valueLabel" content={<EndLabel />} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <GradientBar color={color} />
            </div>

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                metadata={metadata}
                disaggData={disaggData}
                stateData={stateData}
                nationalData={nationalData}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </>
    );
};

export default EthnicityBarChart;